// Shares the CookieYes decision between sepaheld.com and app.sepaheld.com.
//
// CookieYes stores its consent per host, so without this a visitor who
// answered the banner on the website is asked again in the app. Whenever
// CookieYes reports a decision we mirror it into a cookie on the parent domain
// (.sepaheld.com). On load that cookie restores Google Consent Mode before GTM
// starts, and GTM skips the CookieYes tag while it exists (see ANALYTICS.md).
// The website (sepaheld_website, app/layout.js) runs the same logic, so keep
// the cookie name and format identical in both repos.

export const CONSENT_COOKIE = 'sepaheld_consent';
const CONSENT_VERSION = 'v1';
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
const CATEGORIES = ['functional', 'analytics', 'advertisement'];

export const cookieDomain = (hostname = window.location.hostname) =>
    /(^|\.)sepaheld\.com$/.test(hostname) ? '.sepaheld.com' : '';

// "v1|functional:1|analytics:1|advertisement:0"
export const serializeConsent = (accepted = []) =>
    [CONSENT_VERSION, ...CATEGORIES.map((category) => `${category}:${accepted.includes(category) ? 1 : 0}`)].join('|');

export const parseConsent = (value) => {
    if (!value) {
        return null;
    }
    const [version, ...parts] = value.split('|');
    if (version !== CONSENT_VERSION) {
        return null;
    }
    const consent = {};
    parts.forEach((part) => {
        const [category, flag] = part.split(':');
        if (CATEGORIES.includes(category)) {
            consent[category] = flag === '1';
        }
    });
    return CATEGORIES.every((category) => category in consent) ? consent : null;
};

// Same mapping CookieYes uses for Google Consent Mode v2.
export const toGoogleConsent = (consent) => {
    const state = (granted) => (granted ? 'granted' : 'denied');
    return {
        ad_storage: state(consent.advertisement),
        ad_user_data: state(consent.advertisement),
        ad_personalization: state(consent.advertisement),
        analytics_storage: state(consent.analytics),
        functionality_storage: state(consent.functional),
        personalization_storage: state(consent.functional),
    };
};

export const readSharedConsent = () => {
    try {
        const match = document.cookie
            .split('; ')
            .find((entry) => entry.startsWith(`${CONSENT_COOKIE}=`));
        return parseConsent(match ? decodeURIComponent(match.split('=')[1]) : '');
    } catch (error) {
        return null;
    }
};

const domainAttribute = () => {
    const domain = cookieDomain();
    return domain ? `; domain=${domain}` : '';
};

export const writeSharedConsent = (accepted) => {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(serializeConsent(accepted))}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${domainAttribute()}${secure}`;
};

export const clearSharedConsent = () => {
    document.cookie = `${CONSENT_COOKIE}=; path=/; max-age=0; SameSite=Lax${domainAttribute()}`;
};

const acceptedFromCookieYes = () => {
    if (typeof window.getCkyConsent !== 'function') {
        return null;
    }
    const consent = window.getCkyConsent();
    if (!consent?.isUserActionCompleted) {
        return null;
    }
    return CATEGORIES.filter((category) => consent.categories?.[category]);
};

let listenersInitialized = false;

// `gtag` must push an `arguments` object (GTM ignores plain arrays).
export const initConsentBridge = (gtag) => {
    const shared = readSharedConsent();
    if (shared) {
        gtag('consent', 'update', toGoogleConsent(shared));
    }

    if (listenersInitialized) {
        return;
    }
    listenersInitialized = true;

    // A decision made in the CookieYes banner or preference center.
    document.addEventListener('cookieyes_consent_update', (event) => {
        writeSharedConsent(event?.detail?.accepted || []);
    });

    // Visitors who answered before this bridge existed: copy their decision
    // once CookieYes has loaded it.
    document.addEventListener('cookieyes_banner_load', () => {
        const accepted = acceptedFromCookieYes();
        if (accepted && !readSharedConsent()) {
            writeSharedConsent(accepted);
        }
    });

    // "Cookie settings" while CookieYes is skipped (shared consent present):
    // forget the shared decision and reload so the banner asks again.
    document.addEventListener('click', (event) => {
        const trigger = event.target?.closest?.('.cky-banner-element');
        if (trigger && typeof window.getCkyConsent !== 'function') {
            event.preventDefault();
            clearSharedConsent();
            window.location.reload();
        }
    }, true);
};
